import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router"
import { useDebouncedValue } from "./use-debounced-value"

/**
 * List state lives in the URL, so a filtered list is a shareable link and the
 * back button behaves (PDD §7.4). "Send me the link to those failed messages"
 * is a thing colleagues say to each other all day.
 */

export type FilterValue = string | number | boolean | undefined

export type FilterSpec<T extends Record<string, FilterValue>> = {
  [K in keyof T]: {
    /** Absent from the URL when the value equals this. Keeps links short. */
    default: T[K]
    /** Rejects a hand-edited or stale value instead of throwing. */
    parse: (raw: string) => T[K] | undefined
    serialise?: (value: T[K]) => string
  }
}

export const stringFilter = (fallback = ""): FilterSpec<{ v: string }>["v"] => ({
  default: fallback,
  parse: (raw) => raw,
})

export const enumFilter = <T extends string>(allowed: readonly T[], fallback: T | "" = "") => ({
  default: fallback,
  parse: (raw: string): (T | "") | undefined =>
    allowed.includes(raw as T) ? (raw as T) : undefined,
})

export const numberFilter = (fallback: number) => ({
  default: fallback,
  parse: (raw: string): number | undefined => {
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
  },
})

export const boolFilter = (fallback = false) => ({
  default: fallback,
  parse: (raw: string): boolean | undefined =>
    raw === "true" ? true : raw === "false" ? false : undefined,
})

/** The cursor is never part of the filter set; it is derived from it. */
const CURSOR_PARAM = "cursor"

export interface UrlFilters<T extends Record<string, FilterValue>> {
  filters: T
  setFilters: (patch: Partial<T>) => void
  clearFilters: () => void
  /** Filters differing from their default, for the chip row. */
  activeKeys: (keyof T)[]
}

/**
 * A text filter's input box, decoupled from the URL/query it eventually
 * feeds. Every keystroke updates what's ON SCREEN immediately; the URL (and
 * therefore the network query built from it) only updates `delayMs` after
 * typing stops -- otherwise "Marie Ateba" is eleven refetches, one per
 * keystroke, which is what every search box in this dashboard did before
 * this hook existed.
 *
 * `urlValue` is `filters.q` (or similar) from `useUrlFilters`; `onSettle` is
 * `(q) => setFilters({ q })`. When the URL changes from somewhere ELSE --
 * Clear filters, a saved view, the back button -- the input snaps to match
 * rather than showing stale text the URL has already moved past.
 */
export function useDebouncedFilterInput(
  urlValue: string,
  onSettle: (value: string) => void,
  delayMs = 300,
): [string, (value: string) => void] {
  const [input, setInput] = useState(urlValue)
  const debounced = useDebouncedValue(input, delayMs)

  const [syncedFromUrl, setSyncedFromUrl] = useState(urlValue)
  if (urlValue !== syncedFromUrl && urlValue !== debounced) {
    setSyncedFromUrl(urlValue)
    setInput(urlValue)
  }

  useEffect(() => {
    if (debounced !== urlValue) onSettle(debounced)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires only when the debounced value itself settles
  }, [debounced])

  return [input, setInput]
}

export function useUrlFilters<T extends Record<string, FilterValue>>(
  spec: FilterSpec<T>,
): UrlFilters<T> {
  const [params, setParams] = useSearchParams()

  const filters = useMemo(() => {
    const out = {} as T
    for (const key of Object.keys(spec) as (keyof T)[]) {
      const entry = spec[key]
      const raw = params.get(String(key))
      // A malformed or unknown value degrades to the default rather than
      // throwing: a stale bookmark should still open the list.
      const parsed = raw === null ? undefined : entry.parse(raw)
      out[key] = parsed ?? entry.default
    }
    return out
  }, [params, spec])

  const setFilters = useCallback(
    (patch: Partial<T>) => {
      const next = new URLSearchParams(params)
      for (const [key, value] of Object.entries(patch) as [keyof T, T[keyof T]][]) {
        const entry = spec[key]
        const isDefault = value === entry.default || value === undefined || value === ""
        if (isDefault) {
          next.delete(String(key))
        } else {
          next.set(String(key), entry.serialise ? entry.serialise(value) : String(value))
        }
      }
      /**
       * Any filter change REMOVES the cursor. A cursor from the previous filter
       * set points into a different result set; appending page 2 of the new
       * filter onto page 1 of the old one produces a list that is silently
       * wrong (LLD §11.5).
       */
      next.delete(CURSOR_PARAM)
      setParams(next, { replace: true })
    },
    [params, setParams, spec],
  )

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(params)
    for (const key of Object.keys(spec)) next.delete(key)
    next.delete(CURSOR_PARAM)
    setParams(next, { replace: true })
  }, [params, setParams, spec])

  const activeKeys = useMemo(
    () => (Object.keys(spec) as (keyof T)[]).filter((k) => filters[k] !== spec[k].default),
    [filters, spec],
  )

  return { filters, setFilters, clearFilters, activeKeys }
}
